import { useEffect } from 'react';
import { trpc } from '@lib/trpc';

/**
 * Preloader hook to eagerly fetch statuses at app startup
 * Call this in your root component or layout
 */
export const useStatusPreloader = () => {
  const utils = trpc.useContext();

  useEffect(() => {
    // Prefetch statuses immediately on app start
    const prefetchStatuses = async () => {
      try {
        // Prefetch all status-related data in parallel
        await Promise.all([
          utils.status.getAll.prefetch(undefined, {
            staleTime: 15 * 60 * 1000,
            gcTime: 30 * 60 * 1000,
          }),
          utils.status.getChevronData.prefetch(undefined, {
            staleTime: 15 * 60 * 1000,
            gcTime: 30 * 60 * 1000,
          }),
          // Don't prefetch counts as they change more frequently
        ]);
      } catch (error) {
        // Silently handle prefetch errors
      }
    };

    prefetchStatuses();
  }, [utils]);

  return null; // This hook doesn't return anything
};

/**
 * Alternative: Static status data as fallback
 * Use this if you want to show something immediately while real data loads
 */
export const FALLBACK_STATUSES = [
  { key: 'unprocessed', number: 1, label: 'Unprocessed', color: '#ef4444', description: 'New invoices awaiting processing' },
  { key: 'adding', number: 2, label: 'Adding', color: '#f97316', description: 'Adding invoice items to system' },
  { key: 'new_products', number: 3, label: 'New Products', color: '#eab308', description: 'Processing new product entries' },
  { key: 'ir', number: 4, label: 'IR', color: '#84cc16', description: 'Internal requisition processing' },
  { key: 'invoicing', number: 5, label: 'Invoicing', color: '#06b6d4', description: 'Invoice generation and validation' },
  { key: 'head_office_stock', number: 6, label: 'Head Office Stock Invoices', color: '#3b82f6', description: 'Head office stock invoice processing' },
  { key: 'expense_invoices', number: 7, label: 'Expense Invoices', color: '#8b5cf6', description: 'Expense invoice processing' },
  { key: 'entering_stock', number: 8, label: 'Deliver Direct & Head Office Stock Entering', color: '#ec4899', description: 'Stock entry processing' },
  { key: 'entering_expense', number: 9, label: 'Expense Entering', color: '#10b981', description: 'Expense entry processing' },
  { key: 'credit_note', number: 10, label: 'Credit Note', color: '#f59e0b', description: 'Credit note processing' },
  { key: 'credit_entering', number: 11, label: 'Credit Note Entering', color: '#6366f1', description: 'Credit note entry processing' },
  { key: 'completed', number: 12, label: 'Completed', color: '#22c55e', description: 'Processing complete' },
] as const;
